var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { makeAutoObservable, runInAction } from 'mobx';
import { livequeueChatService } from 'ziphy-web-shared/basic/api';
class LivequeueChatStore {
    constructor() {
        this.ids = [];
        this.createdChat = {};
        this.subscriptions = [];
        this.currentChatMessages = [];
        this.chatsList = [];
        this.dashboardItems = [];
        this.isInitialLoading = false;
        this.hasMoreBefore = true;
        this.hasMoreAfter = true;
        // List of message IDs that UI can use to scroll into view after init/load
        this.messagesToScroll = [];
        this.lastViewedMessage = null;
        this.viewerRoleId = null;
        this.viewerRole = null;
        this.isScrolledToBottom = true;
        this.pendingAutoScroll = false;
        this.unreadAnchorId = null;
        this.unreadAnchorVisible = false;
        makeAutoObservable(this);
    }
    isTemporaryMessageId(messageId) {
        return messageId.includes(':');
    }
    getLastDeliveredMessage() {
        for (let i = this.currentChatMessages.length - 1; i >= 0; i -= 1) {
            const candidate = this.currentChatMessages[i];
            if (!this.isTemporaryMessageId(candidate._id)) {
                return candidate;
            }
        }
        return null;
    }
    setViewer(role, roleId) {
        this.viewerRole = role;
        this.viewerRoleId = roleId;
    }
    setScrollState(atBottom) {
        this.isScrolledToBottom = atBottom;
        if (atBottom) {
            const canClear = !this.unreadAnchorId || this.unreadAnchorVisible === false;
            if (canClear) {
                this.clearUnreadState();
            }
        }
    }
    requestScrollToBottom(messageId) {
        this.pendingAutoScroll = true;
        if (messageId) {
            this.messagesToScroll = [messageId];
        }
    }
    setUnreadAnchor(messageId) {
        if (this.isTemporaryMessageId(messageId))
            return;
        if (!this.unreadAnchorId) {
            this.unreadAnchorId = messageId;
            this.unreadAnchorVisible = false;
        }
    }
    clearUnreadState() {
        this.unreadAnchorId = null;
        this.pendingAutoScroll = false;
        this.unreadAnchorVisible = false;
        const latest = this.getLastDeliveredMessage();
        if (latest) {
            this.lastViewedMessage = latest;
        }
    }
    setUnreadAnchorVisible(visible) {
        this.unreadAnchorVisible = visible;
    }
    chatsSearch() {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield livequeueChatService.chatsSearch();
            runInAction(() => {
                this.chatsList = res.data;
            });
        });
    }
    createChat() {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield livequeueChatService.createChat();
            runInAction(() => {
                this.createdChat = res.data;
            });
        });
    }
    getSubscriptions(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield livequeueChatService.getChatsSubscriptions(params);
            runInAction(() => {
                this.subscriptions = data;
            });
            return data;
        });
    }
    getSubscriptionsForChats(chat_ids) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield livequeueChatService.getChatsSubscriptions({ chat_ids });
            runInAction(() => {
                this.subscriptions = this.subscriptions.map((s) => {
                    const chatSubscription = data.find((d) => d.chat_id === s.chat_id);
                    if (chatSubscription) {
                        return Object.assign(Object.assign({}, s), chatSubscription);
                    }
                    return s;
                });
            });
            return data;
        });
    }
    subscribeToChat(chat_id, user_id) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield livequeueChatService.subscribeChat({ chat_id, user_id });
        });
    }
    createMessage(chat_id, content, role_id) {
        return __awaiter(this, void 0, void 0, function* () {
            const tempId = `${chat_id}:${role_id}:${Date.now()}`;
            try {
                runInAction(() => {
                    this.currentChatMessages.push({
                        _id: tempId,
                        created: new Date().toISOString(),
                        modified: new Date().toISOString(),
                        content,
                        chat_id,
                        thread_id: '',
                        role_id: role_id,
                        version: 0,
                        attachments: [],
                        isDraft: true,
                    });
                    this.messagesToScroll = [tempId];
                    this.pendingAutoScroll = true;
                });
                const { data } = yield livequeueChatService.sendChatMessage({ chat_id, content });
                runInAction(() => {
                    // Remove temp and append the server message, avoiding duplicates
                    const withoutTemp = this.currentChatMessages.filter((m) => m._id !== tempId);
                    const alreadyHas = withoutTemp.some((m) => m._id === data._id);
                    this.currentChatMessages = alreadyHas ? withoutTemp : [...withoutTemp, data];
                    this.requestScrollToBottom(data._id);
                });
                // Mark the just sent message as read for the current user
                if (data === null || data === void 0 ? void 0 : data._id) {
                    yield this.markMessageAsRead(chat_id, data._id);
                }
            }
            catch (e) {
                // Remove the temp message on failure
                runInAction(() => {
                    this.currentChatMessages = this.currentChatMessages.filter((m) => m._id !== tempId);
                });
            }
        });
    }
    getChatMessages(params, { prepend = false, append = false, direction, } = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield livequeueChatService.getChatMessages(params);
            runInAction(() => {
                if (prepend) {
                    const all = [...data, ...this.currentChatMessages];
                    const unique = all.filter((m, i, arr) => arr.findIndex((x) => x._id === m._id) === i);
                    this.currentChatMessages = unique;
                }
                else if (append) {
                    const all = [...this.currentChatMessages, ...data];
                    const unique = all.filter((m, i, arr) => arr.findIndex((x) => x._id === m._id) === i);
                    this.currentChatMessages = unique;
                }
                else {
                    this.currentChatMessages = data;
                }
                if (data.length === 0) {
                    if (direction === 'before')
                        this.hasMoreBefore = false;
                    if (direction === 'after')
                        this.hasMoreAfter = false;
                    if (direction === 'initial') {
                        // If initial around LVM returned no data at all, consider both exhausted
                        this.hasMoreBefore = false;
                        this.hasMoreAfter = false;
                    }
                    if (!this.hasMoreBefore) {
                        this.isInitialLoading = true;
                    }
                }
            });
        });
    }
    initChat(chat_id) {
        return __awaiter(this, void 0, void 0, function* () {
            // Reset directional flags for a new chat session
            this.hasMoreBefore = true;
            this.hasMoreAfter = true;
            this.isInitialLoading = false;
            this.messagesToScroll = [];
            this.pendingAutoScroll = false;
            this.unreadAnchorId = null;
            this.isScrolledToBottom = true;
            // Ensure we have subscription for this chat
            yield this.getSubscriptions({ chat_ids: [chat_id] });
            const sub = this.subscriptions.find((s) => s.chat_id === chat_id);
            const lastViewed = sub === null || sub === void 0 ? void 0 : sub.last_viewed_message;
            const pivotId = lastViewed === null || lastViewed === void 0 ? void 0 : lastViewed._id;
            const params = {
                chat_id,
                limit: 20,
            };
            if (pivotId) {
                // Initialize message window before the last viewed message
                params.before_message_id = pivotId;
            }
            yield this.getChatMessages(params, { direction: 'initial' });
            // Ensure last_viewed_message is present and mark it for scrolling
            if (lastViewed === null || lastViewed === void 0 ? void 0 : lastViewed._id) {
                const exists = this.currentChatMessages.some((m) => m._id === lastViewed._id);
                if (!exists) {
                    this.currentChatMessages.push(lastViewed);
                    //or  !?
                    // this.currentChatMessages = [...this.currentChatMessages, lastViewed]
                    // keep unique by _id
                    this.currentChatMessages = this.currentChatMessages.filter((m, i, arr) => arr.findIndex((x) => x._id === m._id) === i);
                }
                this.messagesToScroll = [lastViewed._id];
                this.lastViewedMessage = lastViewed;
            }
        });
    }
    getSubscriptionForChat(chat_id) {
        return this.subscriptions.find((s) => s.chat_id === chat_id);
    }
    markMessageAsRead(chat_id, message_id) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isTemporaryMessageId(message_id)) {
                return;
            }
            try {
                yield livequeueChatService.markMessageAsRead(chat_id, message_id);
                // Update local subscription cache
                const sub = this.subscriptions.find((s) => s.chat_id === chat_id);
                if (sub) {
                    sub.last_viewed_message_id = message_id;
                    sub.unread_count = 0;
                }
                this.unreadAnchorId = null;
                this.pendingAutoScroll = false;
                this.unreadAnchorVisible = false;
                const target = this.currentChatMessages.find((m) => m._id === message_id);
                if (target) {
                    this.lastViewedMessage = target;
                }
            }
            catch (e) { }
        });
    }
    addMessage(message) {
        const exists = this.currentChatMessages.some((m) => m._id === message._id);
        if (!exists) {
            this.currentChatMessages.push(message);
            const isOwn = this.viewerRoleId !== null && message.role_id === this.viewerRoleId;
            if (isOwn || this.isScrolledToBottom) {
                this.requestScrollToBottom(message._id);
            }
            else {
                this.setUnreadAnchor(message._id);
            }
        }
    }
    clearCurrentChatMessages() {
        this.currentChatMessages = [];
        this.messagesToScroll = [];
        this.isInitialLoading = false;
        this.hasMoreBefore = true;
        this.hasMoreAfter = true;
        this.pendingAutoScroll = false;
        this.unreadAnchorId = null;
        this.unreadAnchorVisible = false;
        this.lastViewedMessage = null;
    }
    getDashboardItems(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield livequeueChatService.getDashboardItems(params);
            runInAction(() => {
                this.dashboardItems = data;
            });
        });
    }
}
const livequeueChatStore = new LivequeueChatStore();
export default livequeueChatStore;
